import { useState, useEffect, useMemo, useRef, useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNearbyPlaces, type PlaceCategory, type NearbyPlace } from '../../hooks/useNearbyPlaces';
import { loadGoogleMaps, getGoogleMapsMapId, getMapColorScheme } from '../../utils/googleMapsLoader';
import { PoiIcon, poiIconMarkup } from './poiIcons';

// Place names and addresses come from Google Places and go into InfoWindow
// HTML, so they are escaped like any other outside text.
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// A POI marker that shows its CATEGORY icon in a white badge, so the map reads
// "schools here / mosques here" instead of identical generic dots.
function createCategoryMarker(category: PlaceCategory): HTMLElement {
  const el = document.createElement('div');
  el.style.cssText =
    'width:36px;height:36px;border-radius:9999px;background:#fff;' +
    'border:2px solid #004D40;box-sizing:border-box;box-shadow:0 1px 4px rgba(0,0,0,.3);' +
    'display:flex;align-items:center;justify-content:center;';
  el.innerHTML = poiIconMarkup(category, 20, '#004D40');
  return el;
}

// The property's own marker: a round brand-teal badge with a white house
// glyph, so it reads as "this home".
function createHomeMarker(): HTMLElement {
  const el = document.createElement('div');
  el.style.cssText =
    'width:34px;height:34px;border-radius:9999px;background:#004D40;' +
    'border:3px solid #fff;box-sizing:border-box;box-shadow:0 1px 4px rgba(0,0,0,.35);' +
    'display:flex;align-items:center;justify-content:center;';
  el.innerHTML =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" ' +
    'fill="#fff" aria-hidden="true"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>';
  return el;
}

interface NearbyPlacesProps {
  latitude: number;
  longitude: number;
  searchRadius?: number;
  className?: string;
  showMap?: boolean;
  propertyName?: string;
  propertyAddress?: string;
  /** Slot rendered directly under the map, above the nearby-places list. */
  afterMap?: ReactNode;
}

// The section opens on a short answer - the nearest place of the kinds people
// ask about first - and keeps the full, filterable list behind one button.
const SUMMARY_COUNT = 4;
// Order the summary by what a household checks first, not by API order.
const SUMMARY_PRIORITY: PlaceCategory[] = [
  'WORSHIP',
  'EDUCATION',
  'HEALTHCARE',
  'SHOPPING',
  'DINING',
  'FINANCE',
  'RECREATION',
  'TRANSPORTATION',
];
// Inside the full list: rows shown before «عرض المزيد».
const COLLAPSED_COUNT = 6;
const EXPANDED_COUNT = 20;
// Max POI markers drawn on the map for the selected category. The chip shows the
// full fetched count (up to 20); the map caps markers to keep it readable.
const MAP_MARKER_LIMIT = 10;

// Category labels in English
const CATEGORY_LABELS: Record<PlaceCategory, string> = {
  EDUCATION: 'Education',
  HEALTHCARE: 'Healthcare',
  SHOPPING: 'Shopping',
  DINING: 'Dining',
  RECREATION: 'Recreation',
  WORSHIP: 'Mosques',
  FINANCE: 'Banks',
  TRANSPORTATION: 'Transportation',
};

export default function NearbyPlaces({
  latitude,
  longitude,
  searchRadius = 1500,
  className = '',
  showMap = true,
  propertyName,
  propertyAddress,
  afterMap,
}: NearbyPlacesProps) {
  const { t, i18n } = useTranslation(['property', 'common']);
  const direction = i18n.dir();
  const isRTL = direction === 'rtl';

  const [activeCategory, setActiveCategory] = useState<PlaceCategory | 'ALL'>('ALL');
  const [showAll, setShowAll] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [mapExpanded, setMapExpanded] = useState(true);

  const {
    isLoading,
    isReady,
    error,
    places,
    placesByCategory,
    nearestByCategory,
    getAllNearbyPlaces,
    categories,
    categoryArabic,
  } = useNearbyPlaces({ searchRadius });

  // Fetch places when coordinates change
  useEffect(() => {
    if (isReady && latitude && longitude) {
      getAllNearbyPlaces(latitude, longitude, searchRadius);
    }
  }, [isReady, latitude, longitude, searchRadius, getAllNearbyPlaces]);

  // Get places to show on map based on active category
  // Only show POI markers when a specific category is selected (not ALL)
  // This improves performance by not rendering multiple markers at once
  const mapPlaces = useMemo(() => {
    if (activeCategory === 'ALL') {
      // Don't show any POI markers when ALL is selected - only property marker
      return [];
    }
    // Show up to MAP_MARKER_LIMIT places from the selected category
    return (placesByCategory[activeCategory] || []).slice(0, MAP_MARKER_LIMIT);
  }, [activeCategory, placesByCategory]);

  const categoryPool = useMemo(() => {
    if (activeCategory === 'ALL') return places;
    return placesByCategory[activeCategory] || [];
  }, [activeCategory, places, placesByCategory]);

  const displayedPlaces = useMemo(
    () => categoryPool.slice(0, expanded ? EXPANDED_COUNT : COLLAPSED_COUNT),
    [categoryPool, expanded]
  );

  const summary = useMemo(
    () =>
      SUMMARY_PRIORITY
        .map((category) => ({ category, place: nearestByCategory[category] }))
        .filter((entry): entry is { category: PlaceCategory; place: NearbyPlace } => Boolean(entry.place))
        .slice(0, SUMMARY_COUNT),
    [nearestByCategory]
  );

  // Format distance for display
  const formatDistance = (meters: number): string => {
    if (meters < 1000) {
      return `${meters} ${isRTL ? 'م' : 'm'}`;
    }
    return `${(meters / 1000).toFixed(1)} ${isRTL ? 'كم' : 'km'}`;
  };

  // Format walking time
  const formatWalkingTime = (minutes: number): string => {
    if (minutes < 1) {
      return isRTL ? 'أقل من دقيقة' : '< 1 min';
    }
    return `${minutes} ${isRTL ? 'د' : 'min'}`;
  };

  // Get category label
  const getCategoryLabel = (category: PlaceCategory): string => {
    return isRTL ? categoryArabic[category] : CATEGORY_LABELS[category];
  };

  const countFor = (category: PlaceCategory | 'ALL'): number =>
    category === 'ALL' ? places.length : (placesByCategory[category]?.length || 0);

  const selectCategory = (category: PlaceCategory | 'ALL') => {
    setActiveCategory(category);
    setExpanded(false);
  };

  // A summary card opens the full list already filtered to its kind, which
  // also puts that kind's markers on the map.
  const openCategory = (category: PlaceCategory) => {
    setShowAll(true);
    selectCategory(category);
  };

  const toggleShowAll = () => {
    if (showAll) selectCategory('ALL');
    setShowAll(!showAll);
  };

  const openLocationInMaps = () => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`, '_blank');
  };

  if (!latitude || !longitude) {
    return null;
  }

  const chipClass = (active: boolean) =>
    `flex items-center gap-2 rounded-full border px-4 py-2 text-[15px] font-bold transition-colors ${
      active
        ? 'border-[#004D40] bg-[#004D40] text-white'
        : 'border-[var(--pv-border-soft)] bg-white text-[var(--pv-color-text-muted)] hover:border-[#004D40] hover:text-[#004D40]'
    }`;

  return (
    // A plain section: the property page gives it its hairline and spacing.
    <div className={className}>
      {/* Header */}
      <div className="mb-2 flex items-center justify-between gap-3">
        <h2 className="m-0 flex items-center gap-3 text-[1.25rem] font-semibold text-[var(--pv-color-primary)]">
          {t('property.nearby.title')}
          {isLoading && (
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#004D40] border-t-transparent" aria-hidden />
          )}
        </h2>
        <button
          type="button"
          onClick={openLocationInMaps}
          className="flex flex-shrink-0 items-center gap-1.5 text-[15px] font-bold text-[#004D40] transition hover:text-[var(--pv-color-text)]"
        >
          <i className="ri-map-2-line text-xl leading-none" aria-hidden></i>
          {t('property.nearby.openMap')}
        </button>
      </div>

      {/* Property Address */}
      {propertyAddress && (
        <div className="mb-4 flex items-center gap-2 text-[15px] text-[var(--pv-color-text-muted)]">
          <i className="ri-map-pin-line flex-shrink-0 text-xl leading-none text-[#004D40]" aria-hidden></i>
          <span>{propertyAddress}</span>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-500">
          {error}
        </div>
      )}

      {/* Map Preview */}
      {showMap && latitude && longitude && (
        <div className="mb-4">
          <button
            onClick={() => setMapExpanded(!mapExpanded)}
            className="flex w-full items-center justify-between py-2 text-[15px] font-bold text-[var(--pv-color-text)] transition hover:text-[#004D40]"
          >
            <span className="flex items-center gap-2">
              <i className="ri-road-map-line text-2xl leading-none text-[#004D40]" aria-hidden></i>
              {isRTL ? 'عرض الخريطة' : 'Map View'}
            </span>
            <i className={`ri-arrow-down-s-line text-2xl leading-none transition-transform ${mapExpanded ? 'rotate-180' : ''}`} aria-hidden></i>
          </button>

          {mapExpanded && (
            <POIMapPreview
              latitude={latitude}
              longitude={longitude}
              places={mapPlaces}
              propertyName={propertyName}
              isRTL={isRTL}
            />
          )}
        </div>
      )}

      {/* Daily-places / travel-time slot — sits directly under the map,
          above the nearby-places list. */}
      {afterMap}

      {/* The short answer: the nearest place of each key kind. */}
      {summary.length > 0 && (
        <div className="mt-5">
          <h3 className="m-0 mb-3 text-lg font-bold text-[var(--pv-color-text)]">
            {isRTL ? 'الأقرب إلى العقار' : 'Closest to this property'}
          </h3>
          <ul className="m-0 grid list-none grid-cols-1 gap-2 p-0 sm:grid-cols-2">
            {summary.map(({ category, place }) => (
              <li key={category}>
                <button
                  type="button"
                  onClick={() => openCategory(category)}
                  className="flex w-full items-center gap-3.5 rounded-xl border border-[var(--pv-border-soft)] bg-white p-3 text-start transition-colors hover:border-[#004D40]"
                >
                  <span className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-[var(--pv-color-surface-sunken)] text-[#004D40]">
                    <PoiIcon category={category} size={26} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13.5px] text-[var(--pv-color-text-muted)]">{getCategoryLabel(category)}</span>
                    <span className="block truncate text-base font-bold text-[var(--pv-color-text)]">{place.name}</span>
                  </span>
                  <span className="flex flex-shrink-0 flex-col items-end gap-0.5 text-[13.5px] text-[var(--pv-color-text-muted)]">
                    <b className="text-[15px] text-[#004D40]">{formatDistance(place.distanceMeters)}</b>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-walk-line text-lg leading-none" aria-hidden></i>
                      {formatWalkingTime(place.walkingMinutes)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!isLoading && places.length === 0 && (
        <div className="py-8 text-center text-base text-[var(--pv-color-text-muted)]">
          {isRTL ? 'لا توجد أماكن قريبة' : 'No nearby places found'}
        </div>
      )}

      {/* Everything else, on request */}
      {places.length > 0 && (
        <button
          type="button"
          onClick={toggleShowAll}
          aria-expanded={showAll}
          className="mt-4 flex w-full items-center justify-center gap-1 rounded-xl border border-[var(--pv-border-soft)] bg-white px-4 py-3 text-[15px] font-bold text-[#004D40] transition-colors hover:border-[#004D40] hover:bg-[var(--pv-color-surface-sunken)]"
        >
          {showAll
            ? (isRTL ? 'إخفاء الأماكن القريبة' : 'Hide nearby places')
            : (isRTL ? `عرض كل الأماكن القريبة (${places.length})` : `View all nearby places (${places.length})`)}
          <i className={`${showAll ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-2xl leading-none`} aria-hidden></i>
        </button>
      )}

      {showAll && places.length > 0 && (
        <div className="mt-4">
          {/* Category filter */}
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => selectCategory('ALL')} className={chipClass(activeCategory === 'ALL')}>
              {isRTL ? 'الكل' : 'All'} ({places.length})
            </button>
            {categories.map((category) => {
              const count = countFor(category);
              if (count === 0) return null;
              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => selectCategory(category)}
                  className={chipClass(activeCategory === category)}
                >
                  <PoiIcon category={category} size={20} />
                  {getCategoryLabel(category)} ({count})
                </button>
              );
            })}
          </div>

          {/* Places list */}
          <div className="mt-2 flex flex-col">
            {displayedPlaces.map((place) => (
              <PlaceRow
                key={place.id}
                place={place}
                propertyLatitude={latitude}
                propertyLongitude={longitude}
                isRTL={isRTL}
                formatDistance={formatDistance}
                formatWalkingTime={formatWalkingTime}
              />
            ))}
          </div>

          {categoryPool.length > COLLAPSED_COUNT && (
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="rounded-full px-4 py-2 text-[15px] font-bold text-[#004D40] transition-colors hover:bg-[var(--pv-color-surface-sunken)]"
              >
                {expanded
                  ? t('property.nearby.showLess')
                  : (isRTL ? `عرض المزيد (${categoryPool.length - COLLAPSED_COUNT})` : `Show more (${categoryPool.length - COLLAPSED_COUNT})`)}
                <i className={`${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} mx-1 align-[-3px] text-lg`} aria-hidden></i>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Compact Place Row (click opens driving directions in Google Maps)
// ============================================================================

interface PlaceRowProps {
  place: NearbyPlace;
  propertyLatitude: number;
  propertyLongitude: number;
  isRTL: boolean;
  formatDistance: (meters: number) => string;
  formatWalkingTime: (minutes: number) => string;
}

function PlaceRow({
  place,
  propertyLatitude,
  propertyLongitude,
  isRTL,
  formatDistance,
  formatWalkingTime,
}: PlaceRowProps) {
  const openInMaps = () => {
    const params = new URLSearchParams({
      api: '1',
      origin: `${propertyLatitude},${propertyLongitude}`,
      destination: [place.name, place.address].filter(Boolean).join(', ') || `${place.latitude},${place.longitude}`,
      travelmode: 'driving',
    });
    // place.id is the Google place_id when available; falls back to "<lat>-<lng>" in the hook.
    // Only pass destination_place_id when it's a real Google ID (not the coord fallback).
    const isCoordFallbackId = /^-?\d+(\.\d+)?--?\d+(\.\d+)?$/.test(place.id);
    if (place.id && !isCoordFallbackId) {
      params.set('destination_place_id', place.id);
    }
    window.open(`https://www.google.com/maps/dir/?${params.toString()}`, '_blank');
  };

  return (
    <div
      className="flex cursor-pointer items-center gap-3.5 border-b border-[var(--pv-border-subtle)] px-1 py-3.5 transition-colors hover:bg-[var(--pv-color-surface-soft)]"
      onClick={openInMaps}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') openInMaps(); }}
    >
      {/* Icon */}
      <span className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-[var(--pv-color-surface-sunken)] text-[#004D40]">
        <PoiIcon category={place.category} size={26} />
      </span>

      {/* Name + address + status */}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <b className="truncate text-base text-[var(--pv-color-text)]">{place.name}</b>
        <span className="flex items-center gap-2 truncate text-[13.5px] text-[var(--pv-color-text-muted)]">
          <span className="truncate">{place.address}</span>
          {place.isOpen !== undefined && (
            <span className={`flex-shrink-0 rounded-md px-2 py-0.5 text-[12.5px] font-bold ${
              place.isOpen ? 'bg-[var(--pv-color-surface-sunken)] text-[#1B7566]' : 'bg-[#FDEAEA] text-[#B8171C]'
            }`}>
              {place.isOpen ? (isRTL ? 'مفتوح' : 'Open') : (isRTL ? 'مغلق' : 'Closed')}
            </span>
          )}
        </span>
      </span>

      {/* Rating */}
      {place.rating != null && (
        <span className="hidden flex-shrink-0 items-center gap-1 xs:inline-flex">
          <i className="ri-star-fill text-lg leading-none text-[#B58A0A]" aria-hidden></i>
          <b className="text-[15px] text-[var(--pv-color-text)]">{place.rating.toFixed(1)}</b>
        </span>
      )}

      {/* Distance + times */}
      <span className="flex flex-shrink-0 flex-col items-end gap-1 sm:w-[210px] sm:flex-row sm:items-center sm:justify-end sm:gap-4">
        <b className="text-[15px] text-[#004D40]">{formatDistance(place.distanceMeters)}</b>
        <span className="inline-flex items-center gap-1 whitespace-nowrap text-[14px] text-[var(--pv-color-text-muted)]">
          <i className="ri-walk-line text-lg leading-none" aria-hidden></i>
          {formatWalkingTime(place.walkingMinutes)}
        </span>
        <span className="hidden items-center gap-1 whitespace-nowrap text-[14px] text-[var(--pv-color-text-muted)] md:inline-flex">
          <i className="ri-car-line text-lg leading-none" aria-hidden></i>
          {place.drivingMinutes} {isRTL ? 'د' : 'min'}
        </span>
      </span>
    </div>
  );
}

// ============================================================================
// POI Map Preview Component
// ============================================================================

interface POIMapPreviewProps {
  latitude: number;
  longitude: number;
  places: NearbyPlace[];
  propertyName?: string;
  isRTL: boolean;
}

function POIMapPreview({
  latitude,
  longitude,
  places,
  propertyName,
  isRTL,
}: POIMapPreviewProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | undefined>(undefined);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const [mapReady, setMapReady] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<NearbyPlace | null>(null);

  // Initialize map
  useEffect(() => {
    let cancelled = false;

    async function loadAndInit() {
      try {
        await loadGoogleMaps();
        if (cancelled || !mapRef.current) return;
        initMap();
      } catch (err) {
        console.error('Failed to load Google Maps:', err);
      }
    }

    // Only init synchronously when BOTH the core `Map` constructor and the
    // marker library are ready. Under loading=async the google.maps namespace
    // (and even Map) can exist before marker resolves — creating the Map +
    // AdvancedMarkerElement then crashes with "not an instance of MapsApiMap".
    // Otherwise go through the shared loader, which gates on Map+marker+places.
    if (
      typeof window.google?.maps?.Map === 'function' &&
      typeof window.google?.maps?.marker?.AdvancedMarkerElement === 'function' &&
      mapRef.current
    ) {
      initMap();
    } else {
      loadAndInit();
    }

    return () => { cancelled = true; };

    function initMap() {
      if (!mapRef.current) return;

      const map = new google.maps.Map(mapRef.current, {
        center: { lat: latitude, lng: longitude },
        zoom: 15,
        // Required for AdvancedMarkerElement; POI-label styling moves to
        // cloud-based map styles when a real Map ID is configured.
        mapId: getGoogleMapsMapId(),
        colorScheme: getMapColorScheme(),
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
      });

      mapInstanceRef.current = map;
      setMapReady(true);
    }
  }, [latitude, longitude]);

  // Update markers when places change
  const updateMarkers = useCallback(() => {
    if (!mapInstanceRef.current || !mapReady) return;

    // Clear existing markers
    markersRef.current.forEach((marker) => { marker.map = null; });
    markersRef.current = [];

    const bounds = new google.maps.LatLngBounds();

    // Add property marker (home icon)
    const propertyMarker = new google.maps.marker.AdvancedMarkerElement({
      position: { lat: latitude, lng: longitude },
      map: mapInstanceRef.current ?? undefined,
      title: propertyName || (isRTL ? 'العقار' : 'Property'),
      content: createHomeMarker(),
      zIndex: 1000,
    });

    // Add info window for property
    const propertyInfoWindow = new google.maps.InfoWindow({
      content: `
        <div style="padding: 8px; min-width: 120px;">
          <div style="font-weight: 600; color: #004D40; margin-bottom: 4px;">
            ${escapeHtml(propertyName || (isRTL ? 'العقار' : 'Property'))}
          </div>
          <div style="font-size: 12px; color: #666;">
            ${isRTL ? 'موقع العقار' : 'Property Location'}
          </div>
        </div>
      `,
    });

    propertyMarker.addListener('gmp-click', () => {
      propertyInfoWindow.open(mapInstanceRef.current ?? undefined, propertyMarker as unknown as google.maps.Marker);
      setSelectedPlace(null);
    });

    markersRef.current.push(propertyMarker);
    bounds.extend({ lat: latitude, lng: longitude });

    // Add POI markers — each carries its category's icon.
    places.forEach((place) => {
      const poiMarker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: place.latitude, lng: place.longitude },
        map: mapInstanceRef.current ?? undefined,
        title: place.name,
        content: createCategoryMarker(place.category),
        zIndex: 500,
      });

      // Add info window for POI
      const poiInfoWindow = new google.maps.InfoWindow({
        content: `
          <div style="padding: 8px; min-width: 150px;">
            <div style="font-weight: 600; color: #004D40; margin-bottom: 4px;">
              ${escapeHtml(place.name)}
            </div>
            <div style="font-size: 12px; color: #566B66; margin-bottom: 4px;">
              ${escapeHtml(place.address || '')}
            </div>
            <div style="font-size: 11px; color: #004D40;">
              ${place.distanceMeters < 1000
                ? `${place.distanceMeters}${isRTL ? ' م' : 'm'}`
                : `${(place.distanceMeters / 1000).toFixed(1)}${isRTL ? ' كم' : 'km'}`
              } · ${isRTL ? 'مشياً' : 'walk'} ${place.walkingMinutes}${isRTL ? ' د' : ' min'}
            </div>
          </div>
        `,
      });

      poiMarker.addListener('gmp-click', () => {
        poiInfoWindow.open(mapInstanceRef.current ?? undefined, poiMarker as unknown as google.maps.Marker);
        setSelectedPlace(place);
      });

      markersRef.current.push(poiMarker);
      bounds.extend({ lat: place.latitude, lng: place.longitude });
    });

    // Fit map to show all markers
    if (places.length > 0) {
      mapInstanceRef.current.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      // Don't zoom in too much
      // addListener's return type is declared as `void` in some @types
      // versions even though at runtime it returns a MapsEventListener — cast
      // through unknown so removeListener accepts the value.
      const listener = google.maps.event.addListener(mapInstanceRef.current, 'idle', () => {
        if (mapInstanceRef.current!.getZoom()! > 16) {
          mapInstanceRef.current!.setZoom(16);
        }
        google.maps.event.removeListener(listener as unknown as google.maps.MapsEventListener);
      });
    }
  }, [latitude, longitude, places, propertyName, isRTL, mapReady]);

  useEffect(() => {
    // A map whose key was refused (RefererNotAllowedMapError, quota) throws on
    // every marker call. Without this guard the throw reached React and the
    // whole property page fell to its error screen, e.g. on a language switch
    // re-running this effect; a broken map should only lose its markers.
    try {
      updateMarkers();
    } catch (error) {
      console.warn('[NearbyPlaces] map markers unavailable:', error);
    }
  }, [updateMarkers]);

  return (
    <div className="mt-2 overflow-hidden rounded-[14px] border border-[var(--pv-border-soft)]">
      {/* Map container */}
      <div ref={mapRef} className="h-[280px] w-full bg-[var(--pv-color-surface-sunken)]" />

      {/* Legend */}
      <div className="border-t border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-soft)] p-3">
        <div className="flex flex-wrap items-center gap-4 text-[13.5px]">
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-[#004D40] text-white shadow-sm">
              <i className="ri-home-4-fill text-[13px] leading-none" aria-hidden />
            </span>
            <span className="text-[#617672]">
              {isRTL ? 'العقار' : 'Property'}
            </span>
          </div>
          {places.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="h-5 w-5 rounded-full border-2 border-[#004D40] bg-white shadow-sm" />
              <span className="text-[#617672]">
                {isRTL ? 'الأماكن القريبة' : 'Nearby Places'}
              </span>
            </div>
          )}
          {places.length === 0 && (
            <span className="text-[var(--pv-color-text-muted)]">
              {isRTL ? 'اختر نوعاً من الأماكن القريبة لعرضه على الخريطة' : 'Pick a kind of nearby place to see it on the map'}
            </span>
          )}
          {selectedPlace && (
            <div className="flex flex-1 items-center justify-end gap-1.5 text-[#617672]">
              <PoiIcon category={selectedPlace.category} size={20} className="text-[#004D40]" />
              {selectedPlace.name}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
